{
  "hero": {
    "eyebrow": "Project",
    "title": "Build a radar-assisted semi-autonomous surface monitoring robot.",
    "lead": "AquaWorks is a student-led engineering project developed by students at the University of British Columbia. It creates a reusable platform for location-linked water-quality observation in controlled still-water environments—and, when approved, low-risk sites on the Vancouver campus."
  },
  "purpose": {
    "title": "Overall purpose",
    "body": "Make water-quality conditions easier to observe, understand, and communicate. Each phase builds on the last: adaptability, autonomy, data value, and research potential grow over time."
  },
  "phases": {
    "title": "Development roadmap",
    "items": {
      "phase1": {
        "label": "Phase 1",
        "title": "Radar-assisted still-water monitoring",
        "body": "Primary remote operation with semi-autonomous support for low-speed route following and obstacle-distance awareness. Core sensors, CSV logs, route maps, and at least one spatial visualization. Current AMS SPF application focus.",
        "window": "Sep–Dec 2026 · report Jan 2027"
      },
      "phase2": {
        "label": "Phase 2",
        "title": "Shoreline-adjacent linear monitoring",
        "body": "Move toward approved nearshore routes (for example near an outfall reference). Improve anti-grounding or semi-amphibious structure, GPS tracking, navigation stability, and communication range—subject to permission and safety review."
      },
      "phase3": {
        "label": "Phase 3",
        "title": "Area-based autonomous monitoring",
        "body": "Grid or hotspot routes over selected nearshore areas, stronger amphibious capability, and targeted sampling support for later laboratory analysis of complex pollutants."
      }
    }
  },
  "objectives": {
    "title": "Phase 1 objectives",
    "items": [
      { "title": "Stable modular floating robot", "body": "One integrated prototype with buoyancy, waterproofing, propulsion, payload capacity, and shore retrieval." },
      { "title": "Modular sensing system", "body": "Temperature, pH, turbidity, conductivity, and dissolved oxygen with documented calibration or verification." },
      { "title": "Control, communication, logging", "body": "Reliable remote control, wireless or local storage, synchronized timestamps and spatial references, radar distance in the log." },
      { "title": "Data and visualization workflow", "body": "CSV-compatible export, route maps, trend plots, and at least one spatial water-quality visualization." },
      { "title": "Staged validation", "body": "Bench tests through controlled-water integration; campus water-body tests only with permission and safety approval." },
      { "title": "Complete project archive", "body": "CAD, wiring, software, calibration, safety, maintenance, and final report by January 31, 2027." }
    ]
  },
  "system": {
    "title": "Five coordinated subsystems",
    "lead": "Mechanical floating platform, electrical and power, water-quality sensing, propulsion and control, and data logging with visualization.",
    "workflow": "Route planning → pre-deployment checks → deployment → remote or semi-autonomous operation → measurement → timestamp and location logging → storage or transmission → retrieval → validation → visualization.",
    "items": [
      { "title": "Mechanical platform", "body": "Stable hull or multi-float structure, modular payload area, waterproof enclosure, sensor and propulsion mounts, retrieval features." },
      { "title": "Electrical and power", "body": "Rechargeable battery, regulation, motor drivers, microcontroller, protected wiring, battery monitoring, emergency isolation." },
      { "title": "Water-quality sensing", "body": "Interchangeable indicators for general conditions—not laboratory confirmation of specific contaminants." },
      { "title": "Propulsion and control", "body": "Remote primary mode; radar assists distance awareness. Manual override always available." },
      { "title": "Data and visualization", "body": "Preserve raw files; process into maps and plots with clear assumptions and flagged gaps." }
    ]
  },
  "scope": {
    "title": "Phase 1 in scope",
    "body": "Design, build, integrate, and validate a small modular robot for controlled still-water operation from September 1 to December 31, 2026, with final reporting by January 31, 2027.",
    "sourceTitle": "Public project facts",
    "sourceBody": "AMS Sustainability Projects Fund application. Planned window: Sep 1–Dec 31, 2026. Final report due to AMS Sustainability Office by Jan 31, 2027. Exact funding figures are not published on this site."
  },
  "outOfScope": {
    "title": "Out of scope for Phase 1",
    "items": [
      "Full autonomous navigation or complex mission planning",
      "Open-water, coastal, high-current, or hazardous weather operation",
      "Semi-amphibious / anti-grounding mobility as a required deliverable",
      "Automated physical sampling or laboratory-grade contaminant detection",
      "Regulatory, drinking-water, or public-health monitoring",
      "Unattended long-term deployment or testing without site approval"
    ]
  },
  "principles": {
    "title": "Design principles",
    "items": [
      { "title": "Modularity", "body": "Sensors and major components should be replaceable without rebuilding the whole platform." },
      { "title": "Repairability", "body": "Inspect, maintain, replace, and troubleshoot with clear documentation." },
      { "title": "Safety first", "body": "Low-speed operation, visual supervision, manual override, and reliable retrieval over advanced autonomy." },
      { "title": "Data traceability", "body": "Raw readings, calibration, processing steps, and test conditions stay documented." }
    ]
  },
  "timeline": {
    "title": "Planned Phase 1 timeline",
    "items": {
      "sep": { "label": "Sep 2026", "title": "Setup and verification", "body": "Finalize requirements, CAD, electrical architecture, sensor plan, safety procedures, and component checks." },
      "oct": { "label": "Oct 2026", "title": "Subsystem build", "body": "Fabricate platform; bench-test electronics; establish software modules for sensors, control, and logging." },
      "nov": { "label": "Nov 2026", "title": "Integration and controlled water", "body": "Buoyancy, waterproofing, propulsion, calibration, and first synchronized datasets with visualization pipeline." },
      "dec": { "label": "Dec 2026", "title": "Full-system validation", "body": "Controlled-water test event; optional approved campus still-water demo if permission and conditions allow." },
      "jan": { "label": "Jan 2027", "title": "Report and handoff", "body": "Final report, technical archive, maintenance guide, and recommendations for future phases." }
    }
  },
  "disclaimer": {
    "title": "Honest limits",
    "body": "AquaWorks does not replace professional environmental assessment, regulatory monitoring, or certified laboratory testing. It is a student platform for spatial learning, preliminary observation, and reusable technical foundations."
  },
  "cta": {
    "primary": "Explore the science",
    "secondary": "Meet the teams"
  }
}
